/**
 * Device profiles and decoy presets for the Simulated Context
 * (docs/sandbox.md §5, ADR-0017). Each profile is a coherent identity: the
 * user agent, platform, hardware, and screen agree with each other.
 */
import type { DeviceProfile } from "../../context";

const CHROME = 141;

export const DEVICE_PROFILES: Readonly<Record<string, { label: string; device: DeviceProfile }>> = {
  "chrome-macos": {
    label: "Chrome on macOS (laptop)",
    device: {
      userAgent: `Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${CHROME}.0.0.0 Safari/537.36`,
      platform: "MacIntel",
      uaPlatform: "macOS",
      mobile: false,
      chromeVersion: CHROME,
      hardwareConcurrency: 8,
      deviceMemory: 8,
      maxTouchPoints: 0,
      screen: { width: 1512, height: 982, availWidth: 1512, availHeight: 944, colorDepth: 30 },
      devicePixelRatio: 2,
      viewport: { innerWidth: 1512, innerHeight: 829, outerWidth: 1512, outerHeight: 944 },
    },
  },
  "chrome-windows": {
    label: "Chrome on Windows (desktop)",
    device: {
      userAgent: `Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${CHROME}.0.0.0 Safari/537.36`,
      platform: "Win32",
      uaPlatform: "Windows",
      mobile: false,
      chromeVersion: CHROME,
      hardwareConcurrency: 12,
      deviceMemory: 8,
      maxTouchPoints: 0,
      screen: { width: 1920, height: 1080, availWidth: 1920, availHeight: 1032, colorDepth: 24 },
      devicePixelRatio: 1,
      viewport: { innerWidth: 1920, innerHeight: 945, outerWidth: 1920, outerHeight: 1032 },
    },
  },
  "chrome-android": {
    label: "Chrome on Android (phone)",
    device: {
      userAgent: `Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${CHROME}.0.0.0 Mobile Safari/537.36`,
      platform: "Linux armv81",
      uaPlatform: "Android",
      mobile: true,
      chromeVersion: CHROME,
      hardwareConcurrency: 8,
      deviceMemory: 4,
      maxTouchPoints: 5,
      screen: { width: 412, height: 915, availWidth: 412, availHeight: 915, colorDepth: 24 },
      devicePixelRatio: 2.625,
      viewport: { innerWidth: 412, innerHeight: 839, outerWidth: 412, outerHeight: 915 },
    },
  },
};

export const DEFAULT_DEVICE_PROFILE = "chrome-macos";

/** Decoy presets: which third-party globals to fake. */
export const DECOY_PRESETS: Readonly<Record<string, { label: string; decoys: readonly string[] }>> =
  {
    bare: { label: "Bare page", decoys: [] },
    storefront: {
      label: "Typical storefront",
      decoys: ["ga", "gtag", "dataLayer", "jQuery", "$", "Stripe"],
    },
    crypto: { label: "Crypto user", decoys: ["ethereum"] },
    developer: {
      label: "Developer",
      decoys: ["__REACT_DEVTOOLS_GLOBAL_HOOK__", "chrome.runtime"],
    },
  };

export const DEFAULT_DECOY_PRESET = "storefront";

/** Every decoy the prelude knows how to fake. */
export const KNOWN_DECOYS: readonly string[] = [
  ...new Set(Object.values(DECOY_PRESETS).flatMap((preset) => preset.decoys)),
];

/** The preset whose decoys are exactly `decoys`, if any. */
export function decoyPresetOf(decoys: readonly string[]): string | null {
  for (const [id, preset] of Object.entries(DECOY_PRESETS)) {
    if (
      preset.decoys.length === decoys.length &&
      preset.decoys.every((name) => decoys.includes(name))
    ) {
      return id;
    }
  }
  return null;
}
